import { MultiSelectProps } from '../input.interface';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';

import { Chip, ChipProps } from 'common/components/Chips/Chip';
import MUIAutocomplete from '@mui/material/Autocomplete';
import { TextOption } from '../Autocomplete/Components/Options/TextOption';
import { useMemo } from 'react';
import { BlankOption } from '../Autocomplete/Components/Options/BlankOption';

import BlankIcon from 'assets/images/blank.svg';
import { omit } from 'lodash';
import { pxToRem } from 'src/common/utils/fonts';

const TagBox = styled(Box)(({ theme }) => ({
  '& .MuiChip-label': {
    maxWidth: theme.spacing(260),
  },
}));

const OptionListItem = styled('li')<{ selected: boolean }>(({ selected, theme }) => ({
  height: theme.spacing(36),
  paddingLeft: theme.spacing(8),
  paddingRight: theme.spacing(6),
  backgroundColor: selected ? theme.palette.grey[200] : theme.palette.background.paper,

  display: 'flex',
  alignItems: 'center',
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
}));

const TextOptionList = styled('div')({
  width: '100%',
  textWrap: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
});

export type SelectProps<T> = MultiSelectProps & {
  showSelectAll: boolean;
  options: { label: string; value: T }[];
  value: T[];
  onChange?: (values: T[]) => void;
  placeholder?: string;
  variant?: ChipProps['variant'] | 'text';
};

const SELECT_ALL = Symbol('SELECT_ALL');
const BLANK = Symbol('BLANK');

export const MultiSelect = <T extends string | number | null = string>({
  onChange,
  value,
  showSelectAll,
  options,
  placeholder,
  variant = 'filled',
  ...props
}: SelectProps<T>) => {
  const { t } = useTranslation();

  const handleOnChange = (_: React.SyntheticEvent, value: { label: string; value: T }[]) => {
    if (onChange) {
      onChange(value.map((value) => value.value));
    }
  };

  const values = useMemo(() => {
    return options.filter((option) => value.includes(option.value));
  }, [value, options]);

  return (
    <MUIAutocomplete
      {...props}
      disableClearable
      sx={{
        bgcolor: (theme) => theme.palette.background.paper,
        '.MuiAutocomplete-inputRoot': {
          py: 5,
          pl: 12,
          height: 'auto',
          minHeight: pxToRem(40),
          pr: `${pxToRem(30)} !important`,
        },
        ...props.sx,
      }}
      options={options}
      multiple
      value={values}
      disableCloseOnSelect
      isOptionEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
      onChange={handleOnChange}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                readOnly: true,
                sx: {
                  p: '0 ',
                  position: value.length > 0 ? 'absolute' : 'initial',
                  paddingTop: '0 !important',
                  paddingBottom: '0 !important',
                },
              },
            }}
            placeholder={value.length > 0 ? '' : placeholder}
          />
        );
      }}
      renderTags={(selectedOptions, getTagsProps) => {
        if (variant === 'text') {
          return (
            <TextOptionList>
              {selectedOptions.map((option) => option.label).join(', ')}
            </TextOptionList>
          );
        }

        return selectedOptions.map((option, index) => {
          const { onDelete, ...tagProps } = getTagsProps({ index });
          return (
            <TagBox {...tagProps} data-testid="selectedTags">
              <Chip
                label={option.label}
                onDelete={onDelete}
                variant={variant}
                icon={option.value === null ? <BlankIcon /> : undefined}
              />
            </TagBox>
          );
        });
      }}
      //@ts-expect-error --  T could be a subtype of the constraint
      filterOptions={(options) => {
        if (showSelectAll) {
          return [{ value: SELECT_ALL, label: t('common.selectAll') }, ...options];
        }

        return options;
      }}
      renderOption={(props, option, { selected }, { value, options }) => {
        const filteredProps = omit(props, ['className', 'children']);
        filteredProps.key = option.value;

        const commonProps = {
          name: option.label,
          selected,
        };

        if (option.value === null) {
          return (
            <OptionListItem selected={selected} {...filteredProps} key={BLANK.toString()}>
              <Box flex={1}>
                <BlankOption {...commonProps} />
              </Box>
            </OptionListItem>
          );
        }

        //@ts-expect-error -- SELECT_ALL symbol is not assignable to T
        if (option.value === SELECT_ALL) {
          const allSelected = options?.length === value?.length;

          return (
            <OptionListItem
              {...filteredProps}
              key={filteredProps.key.toString()}
              sx={{ borderBottom: ({ palette }) => `1px solid ${palette.border}` }}
              aria-label={t('common.selectAll')}
              aria-selected={allSelected}
              selected={false}
              onClick={(e) => {
                if (allSelected) {
                  handleOnChange(e, []);
                } else {
                  handleOnChange(e, [...options]);
                }
              }}
            >
              <Box flex={1}>
                <TextOption {...commonProps} selected={allSelected} />
              </Box>
            </OptionListItem>
          );
        }

        return (
          <OptionListItem selected={selected} {...filteredProps}>
            <Box flex={1}>
              <TextOption {...commonProps} />
            </Box>
          </OptionListItem>
        );
      }}
    />
  );
};
